import {
  NotificationReceiver,
  ParentalConsent,
  TwoFactorAuthenticationReceiver,
  User,
} from '../../domains/User';
import { IUserRepository } from './IUserRepository';
import { A21 as U002A21, A22 as U002A22 } from '../../api/U002';
import { A01, A02, A03 } from '../../api/U006';
import { PhoneNumber } from '../../domains/PhoneNumber';

export class UserRepository implements IUserRepository {
  /**
   * Retrieves user information.
   *
   * @param {string} userId - The ID of the user to retrieve information for.
   * @param {AbortSignal} cancellation - The signal used for cancelling the request.
   * @return {Promise<User>} A Promise that resolves to the User object containing the retrieved information.
   */
  async get(
    userId: string,
    cancellation?: AbortSignal
  ): Promise<{
    user: User;
    applicant_revision: number;
    user_info_revision: number;
  }> {
    const res = await A01({ user_id: userId }, cancellation);

    let birthday = new Date(1990, 1, 1);
    if (typeof res.birth === 'string') {
      birthday = new Date(res.birth);
    }

    return {
      user: new User(
        res.user_id,
        res.firstname,
        res.lastname,
        res.firstname_kana,
        res.lastname_kana,
        res.notification as NotificationReceiver,
        res.mfa as TwoFactorAuthenticationReceiver,
        birthday,
        res.mail,
        new PhoneNumber(res.phone1),
        (res.parental_consent as ParentalConsent) ?? undefined,
        res.consent_form,
        res.line_id,
        res.line_nonce
      ),
      applicant_revision: res.applicant_revision,
      user_info_revision: res.user_info_revision,
    };
  }
  /**
   * Creates a new user.
   * @param {User} user - The user to create.
   * @param {AbortSignal} cancellation - The signal to abort the creation.
   *
   * @returns {Promise<UpdateResponse>} - A promise that resolves to the result of the creation.
   */
  async create(
    user: User,
    password: string,
    sessionId: string,
    lang: string,
    path: string,
    cancellation: AbortSignal
  ): Promise<{
    status: string;
    msgid: string;
  }> {
    const params = {
      user_id: user.id,
      phone1: user.phone?.value ?? '',
      password: password,
      session_id: sessionId,
      lang: lang,
      path: path,
    };

    return await U002A21(params, cancellation);
  }

  /**
   * Add new user detail.
   * @param {User} user - The user detail to update.
   * @param {AbortSignal} cancellation - The signal to abort the creation.
   *
   * @returns {Promise<UpdateResponse>} - A promise that resolves to the result of the creation.
   */
  async detailUpdate(
    username: string,
    firstName: string,
    lastName: string,
    firstNameKana: string,
    lastNameKana: string,
    birthday: Date,
    mailAddress: string,
    cancellation: AbortSignal,
    parentName?: string,
    parentPhoneNumber?: string,
    auditionId?: string
  ): Promise<{
    status: string;
    msgid: string;
  }> {
    const params = {
      user_id: username,
      firstname: firstName,
      lastname: lastName,
      firstname_kana: firstNameKana,
      lastname_kana: lastNameKana,
      birth: birthday,
      mail: mailAddress,
      parent_name: parentName ?? '',
      parent_phone: parentPhoneNumber ?? '',
      audition_id: auditionId ?? '',
    };

    return await U002A22(params, cancellation);
  }

  /**
     * Updates the user asynchronously.
     * @param {User} user - The user to update.
     * @param {AbortSignal} cancellation - The cancellation signal.
    //  * @returns {Promise<User>} A promise that resolves with the updated user.
     */
  async update(
    user: User,
    revision: number,
    cancellation: AbortSignal
  ): Promise<{
    status: string;
    msgid: string;
  }> {
    const params = {
      user_id: user.id,
      firstname: user.firstName,
      lastname: user.lastName,
      firstname_kana: user.firstNameKana,
      lastname_kana: user.lastNameKana,
      birth: user.birthDay.toLocaleDateString('ja-JP'),
      mfa: user.twoFactorAuthReceiver,
      notification: user.notificationReceiver,
      phone1: user.phone?.value ?? '',
      mail: user.mailAddress,
      revision: revision,
    };
    return await A02(params, cancellation);
  }

  /**
   * Deletes a user.
   *
   * @param {string} userId - The ID of the user to delete.
   * @param {AbortSignal} cancellation - The signal to abort the deletion.
   * @returns {Promise<UpdateResponse>} - A promise that resolves to the number of deleted users.
   */
  async delete(userId: string, applicant_revision: number, user_info_revision: number) {
    return await A03({
      user_id: userId,
      applicant_revision: applicant_revision,
      user_info_revision: user_info_revision,
    });
  }
}
